import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import api from "./lib/api";
import AuthCallback from "./components/AuthCallback";
import { Navbar } from "./components/Navbar";
import { TickerTape } from "./components/TickerTape";
import { Footer } from "./components/Footer";
import { Sunburst } from "./components/Sunburst";
import { ChatWidget } from "./components/ChatWidget";
import { AccessGate, hasConfirmedAccess } from "./components/AccessGate";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ShopCategory from "./pages/ShopCategory";
import ProductDetail from "./pages/ProductDetail";
import CartPage from "./pages/CartPage";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import MyOrders from "./pages/MyOrders";
import AdminPage from "./pages/admin/AdminPage";
import UnderConstruction from "./pages/UnderConstruction";
import AuthPage from "./pages/AuthPage";
import LabResults from "./pages/LabResults";
import CalculatorPage from "./pages/CalculatorPage";
import Bundles from "./pages/Bundles";
import Research from "./pages/Research";
import GuidePage from "./pages/GuidePage";

function AppRouter() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [siteMode, setSiteMode] = useState(null);
  const [accessConfirmed, setAccessConfirmed] = useState(hasConfirmedAccess);

  useEffect(() => {
    api.get("/site-mode")
      .then(({ data }) => setSiteMode(data))
      .catch(() => setSiteMode({ under_construction: false }));
  }, []);

  useEffect(() => {
    const ref = (new URLSearchParams(location.search).get("ref") || "").trim().toUpperCase();
    if (!ref || !/^[A-Z0-9_-]{2,40}$/.test(ref)) return;
    localStorage.setItem("eb_ref", JSON.stringify({ code: ref, expires: Date.now() + 30 * 24 * 3600 * 1000 }));
    toast.success(`Discount code ${ref} will be applied at checkout`);
  }, [location.search]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || location.hash?.includes("session_id=")) return;
    let vid = localStorage.getItem("eb_vid");
    if (!vid) { vid = crypto.randomUUID(); localStorage.setItem("eb_vid", vid); }
    let sid = sessionStorage.getItem("eb_sid");
    if (!sid) { sid = crypto.randomUUID(); sessionStorage.setItem("eb_sid", sid); }
    const ref = document.referrer && !document.referrer.startsWith(window.location.origin) ? document.referrer : "";
    api.post("/track", { visitor_id: vid, session_id: sid, path: location.pathname, referrer: ref }).catch(() => {});
  }, [location.pathname, location.hash]);

  // Check URL fragment for session_id BEFORE any routing (auth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (!accessConfirmed) {
    return <AccessGate onEnter={() => setAccessConfirmed(true)} />;
  }

  if (siteMode === null || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="site-mode-loading">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (siteMode.under_construction && !user?.is_admin) {
    return <UnderConstruction />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Sunburst />
      <TickerTape />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:slug" element={<ShopCategory />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:id" element={<OrderConfirmation />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/lab-results" element={<LabResults />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/bundles" element={<Bundles />} />
          <Route path="/research" element={<Research />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
            <Toaster position="bottom-right" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
